import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import styled, { css } from 'styled-components';

import { ssrStateKey, useSsrState } from 'shared/hooks/useSsrState';
import { setSsrField } from 'shared/helpers/ssrCache';

import {
  handleExpandSSRDetail,
  selectEssSwitch,
} from 'shared/slice/essSwitchSlice';
import {
  selectTesSwitch,
  tesHandleExpandSSRDetail,
} from 'shared/slice/tesSwitchSlice';
import {
  setSsrSettingOpen,
  setSsrPasswordBox,
  selectSsrUiEntry,
} from 'shared/slice/ssrUiSlice';
import {
  selectUserPermissions,
  selectUserInfo,
  handleAccessAdministrator,
  handlePasswordPropagation,
} from 'shared/slice/userSlice';
import {
  updateSwitchSettingService,
  updateBlowerSettingService,
} from 'shared/services';
import {
  essCommandService,
  tesCommandService,
} from 'features/masterControl/services/sendCommand.service';
import AdminSSRItemDetails from 'shared/components/commonComponentsMC/AdminSSRItemDetails';
import ContainerLogin from 'shared/components/adminPassword/ContainerLogin';

const TC_OPTIONS = [
  'tc-01', 'tc-02', 'tc-03', 'tc-04', 'tc-05', 'tc-06',
  'tc-07', 'tc-08', 'tc-09', 'tc-10', 'tc-11',
];

const getSsrNumber = (key) => Number(key.replace('ssr', '')) || 0;

const SwitchHeaterStatus = ({ swtName, location, machine, disabled: parentDisabled }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const permissions = useSelector(selectUserPermissions);
  const { isAdministrator, isPasswordBoxOpen } = useSelector(selectUserInfo);
  const writeDisabled = !permissions.WRITE || parentDisabled;
  const commandService = swtName === 'ess' ? essCommandService : tesCommandService;

  const essData = useSelector(selectEssSwitch);
  const tesData = useSelector(selectTesSwitch);
  const flatSwitch = swtName === 'ess' ? essData.flatEssSwitch : tesData.flatTesSwitch;
  const switchStatus = flatSwitch?.[location]?.[machine];

  const {
    deviceMac,
    heaterThermocoupleMap,
    activeThermocouples,
    activatedHeaters,
    switch_panels,
    isExpanded,
  } = switchStatus || {};


  const ssrState = useSsrState(machine);

  const [localActivatedHeaters, setLocalActivatedHeaters] = useState(activatedHeaters);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (activatedHeaters) setLocalActivatedHeaters(activatedHeaters);
  }, [activatedHeaters]);

  const ssrs = Object.entries(ssrState || {})
    .sort(([a], [b]) => getSsrNumber(a) - getSsrNumber(b))
    .map(([key, data]) => ({ key, id: getSsrNumber(key), data }));

  const handleExpand = () => {
    const action = swtName === 'ess' ? handleExpandSSRDetail : tesHandleExpandSSRDetail;
    dispatch(action({ location, machine, status: !isExpanded }));
  };



  const handleForceReset = (ssrData) => {
    if (!isAdministrator) return;
    if (ssrData?.reset === 1 && ssrData?.fault) {
      commandService(ssrData.device_id || deviceMac, {
        ssr_no: ssrData.No,
        ssr_reset: 0,
      });
    }
  };



  const handleOpenSsrSettings = (ssrId, openPasswordBox) => {
    const id = `ssr${ssrId}`;
    if (isAdministrator) {
      dispatch(setSsrSettingOpen({ swtName, location, machine, id, status: true }));
    } else if (openPasswordBox) {
      dispatch(setSsrPasswordBox({ swtName, location, machine, id, status: false }));
    } else if (!isPasswordBoxOpen) {
      dispatch(setSsrPasswordBox({ swtName, location, machine, id, status: true }));
      dispatch(handlePasswordPropagation(true));
    }
  };

  const handleCloseSsrSettings = (ssrId) => {
    dispatch(setSsrSettingOpen({ swtName, location, machine, id: `ssr${ssrId}`, status: false }));
    dispatch(handleAccessAdministrator(false));
  };

  const handleCloseSsrPassword = (ssrId) => {
    dispatch(setSsrPasswordBox({ swtName, location, machine, id: `ssr${ssrId}`, status: false }));
    dispatch(handlePasswordPropagation(false));
  };


  const handleConfirmSsrSettings = (ssrId) => {
    dispatch(setSsrSettingOpen({ swtName, location, machine, id: `ssr${ssrId}`, status: true }));
  };

  const handleToggle = useCallback((ssrId, newStatus) => {
    if (isUpdating || writeDisabled) return;
    setIsUpdating(true);

    const newHeaters = {
      activated_heaters: (localActivatedHeaters || []).map((val, i) =>
        i === ssrId - 1 ? (newStatus === 'on' ? 1 : 0) : val
      ),
    };

    const updateService = swtName === 'ess' ? updateSwitchSettingService : updateBlowerSettingService;

    updateService([deviceMac], newHeaters)
      .then((res) => {
        const serverHeaters = res?.[0]?.activated_heaters;
        const matches = !serverHeaters?.some(
          (val, i) => val !== newHeaters.activated_heaters[i]
        );
        if (matches) {
          queryClient.setQueryData(ssrStateKey(machine), (prev) =>
            setSsrField(prev, `ssr${ssrId}`, 'buttonStatus', newStatus)
          );
          if (serverHeaters) setLocalActivatedHeaters(serverHeaters);
        }
      })
      .catch((err) => console.error('toggle SSR fail:', err))
      .finally(() => setIsUpdating(false));
  }, [isUpdating, writeDisabled, localActivatedHeaters, deviceMac, swtName, queryClient, location, machine]);

  const handleSelectTC = useCallback((ssrId, tcValue) => {
    if (writeDisabled) return;

    const newMap = {
      heater_thermocouple_map: (heaterThermocoupleMap || []).map((val, i) =>
        i === ssrId - 1 ? Number(tcValue.slice(-2)) : val
      ),
    };

    const updateService = swtName === 'ess' ? updateSwitchSettingService : updateBlowerSettingService;

    updateService([deviceMac], newMap).then(() => {
      queryClient.setQueryData(ssrStateKey(machine), (prev) =>
        setSsrField(prev, `ssr${ssrId}`, 'select', tcValue)
      );
    });
  }, [writeDisabled, heaterThermocoupleMap, deviceMac, swtName, queryClient, location, machine]);

  if (!ssrState || Object.keys(ssrState).length === 0) return null;

  return (
    <Section>
      <SectionHeader>
        <HeaderLeft>
          <HeaderTitle>HEATER STATUS</HeaderTitle>
          <LinkIcon viewBox="0 0 16 16" fill="none">
            <path d="M6.5 9.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1" stroke="#2B7FFF" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M9.5 6.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1" stroke="#2B7FFF" strokeWidth="1.2" strokeLinecap="round" />
          </LinkIcon>
        </HeaderLeft>
        <ExpandBtn onClick={handleExpand}>
          {isExpanded ? 'Collapse' : 'Expand'}
        </ExpandBtn>
      </SectionHeader>

      <ChipsRow>
        {ssrs.map(({ key, data }) => {
          const status = data?.buttonStatus || 'off';
          const canReset = isAdministrator && data?.reset === 1 && !!data?.fault;
          return (
            <SsrChip
              key={key}
              as="button"
              type="button"
              $warn={data?.warn === 1}
              $test={data?.test === 1}
              $canReset={canReset}
              disabled={!canReset}
              onClick={() => handleForceReset(data)}
              title={canReset ? 'Reset SSR fault' : undefined}
            >
              <ChipLabel>SSR {getSsrNumber(key)}</ChipLabel>
              <ChipBadge $isOn={status === 'on'} $isFault={status === 'flt'}>{status}</ChipBadge>
            </SsrChip>
          );
        })}
      </ChipsRow>

      {isExpanded && (
        <ExpandedArea>
          {ssrs.map(({ key, id, data }) => (
            <SsrDetailCard
              key={key}
              ssrId={id}
              data={data}
              location={location}
              machine={machine}
              swtName={swtName}
              isAdministrator={isAdministrator}
              activeThermocouples={activeThermocouples}
              switchPanels={switch_panels}
              writeDisabled={writeDisabled}
              isUpdating={isUpdating}
              onToggle={handleToggle}
              onSelectTC={handleSelectTC}
              onOpenSettings={handleOpenSsrSettings}
              onCloseSettings={handleCloseSsrSettings}
              onClosePassword={handleCloseSsrPassword}
              onConfirmSettings={handleConfirmSsrSettings}
            />
          ))}
        </ExpandedArea>
      )}
    </Section>
  );
};

export default SwitchHeaterStatus;

const SsrDetailCard = ({
  ssrId,
  data,
  location,
  machine,
  swtName,
  isAdministrator,
  activeThermocouples,
  switchPanels,
  writeDisabled,
  isUpdating,
  onToggle,
  onSelectTC,
  onOpenSettings,
  onCloseSettings,
  onClosePassword,
  onConfirmSettings,
}) => {
  const [tcOpen, setTcOpen] = useState(false);
  const [tcSelected, setTcSelected] = useState(data?.select || TC_OPTIONS[0]);
  const [ssrSwitchName, setSsrSwitchName] = useState('');
  const status = data?.buttonStatus || 'off';
  const isOn = status === 'on';
  const isFault = status === 'flt';
  const isWarn = data?.warn === 1;
  const { isSettingOpen, openPasswordBox } = useSelector((s) =>
    selectSsrUiEntry(s, { swtName, location, machine, id: `ssr${ssrId}` })
  );
  const specs = data?.specs || [];


  const ratedRef = data?.specs?.[0]?.current;
  const actualRef = data?.specs?.[0]?.currentSSR ?? data?.specs?.[0]?.currentCurrent;
  const isOverAmp =
    typeof ratedRef === 'number' &&
    typeof actualRef === 'number' &&
    (actualRef > ratedRef * 1.2 || actualRef < ratedRef * 0.8);

  useEffect(() => {
    if (data?.select) setTcSelected(data.select);
  }, [data?.select]);

  const panelName = switchPanels?.find((p) => p.system_id === data?.switch_system_id)?.name || '';

  const handleToggleClick = (newStatus) => {
    if (isFault || isUpdating || writeDisabled) return;
    onToggle(ssrId, newStatus);
  };

  const handleTcConfirm = () => {
    onSelectTC(ssrId, tcSelected);
    setTcOpen(false);
  };

  return (
    <SsrDetail $overAmp={isOverAmp} $isWarn={isWarn} $isFault={isFault}>
      <SsrDetailHeader>
        <SsrIdGroup>
          <SsrDetailName>SSR {ssrId}</SsrDetailName>
          {panelName && <PanelLabel>{panelName}</PanelLabel>}
        </SsrIdGroup>

        <ToggleGroup>
          <ToggleBtn
            $active={isOn}
            $variant="on"
            onClick={() => handleToggleClick('on')}
            disabled={isFault || isUpdating || writeDisabled}
          >
            ON
          </ToggleBtn>
          <ToggleBtn
            $active={!isOn && !isFault}
            $variant="off"
            onClick={() => handleToggleClick('off')}
            disabled={isFault || isUpdating || writeDisabled}
          >
            OFF
          </ToggleBtn>
        </ToggleGroup>

        <TcDropdownWrapper>
          <TcButton
            type="button"
            onClick={() => !isFault && setTcOpen(!tcOpen)}
            disabled={isFault}
          >
            <span>select</span>
            <ChevronSvg viewBox="0 0 12 12" fill="none" $open={tcOpen}>
              <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </ChevronSvg>
          </TcButton>

          {tcOpen && (
            <TcDropdown>
              {TC_OPTIONS.map((tc, i) => {
                const isDisabled = activeThermocouples?.[i] === 0;
                const isActive = tcSelected === tc;
                return (
                  <TcOption
                    key={tc}
                    type="button"
                    $active={isActive}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && setTcSelected(tc)}
                  >
                    <TcDot $active={isActive} />
                    {tc}
                  </TcOption>
                );
              })}
              <TcConfirmBtn type="button" onClick={handleTcConfirm}>Select</TcConfirmBtn>
            </TcDropdown>
          )}
        </TcDropdownWrapper>

        <SettingsBtn
          type="button"
          $active={isSettingOpen}
          onClick={() =>
            isSettingOpen ? onCloseSettings(ssrId) : onOpenSettings(ssrId, openPasswordBox)
          }
          title="SSR heater settings"
        >
          <GearSvg viewBox="0 0 16 16" fill="none">
            <path
              d="M8 10a2 2 0 100-4 2 2 0 000 4z"
              stroke="currentColor"
              strokeWidth="1.2"
            />
            <path
              d="M13.3 9.5a1 1 0 00.2 1.1l.04.04a1.2 1.2 0 11-1.7 1.7l-.04-.04a1 1 0 00-1.1-.2 1 1 0 00-.6.92V13a1.2 1.2 0 01-2.4 0v-.05a1 1 0 00-.66-.92 1 1 0 00-1.1.2l-.04.04a1.2 1.2 0 11-1.7-1.7l.04-.04a1 1 0 00.2-1.1 1 1 0 00-.92-.6H3a1.2 1.2 0 010-2.4h.05a1 1 0 00.92-.66 1 1 0 00-.2-1.1l-.04-.04a1.2 1.2 0 111.7-1.7l.04.04a1 1 0 001.1.2H7.7a1 1 0 00.6-.92V3a1.2 1.2 0 012.4 0v.05a1 1 0 00.6.92 1 1 0 001.1-.2l.04-.04a1.2 1.2 0 111.7 1.7l-.04.04a1 1 0 00-.2 1.1V6.3a1 1 0 00.92.6H15a1.2 1.2 0 010 2.4h-.05a1 1 0 00-.92.6z"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </GearSvg>
        </SettingsBtn>
      </SsrDetailHeader>

      {isSettingOpen ? (
        <AdminEditorWrapper>
          <AdminSSRItemDetails
            isEnable={isOn}
            isFault={isFault}
            isWarn={isWarn}
            option={2}
            id={ssrId}
            data={data}
            isSettingOpen={isSettingOpen}
            handleButtonClick={() => onCloseSettings(ssrId)}
            isAdministrator={isAdministrator}
            location={location}
            machine={machine}
            swtName={swtName}
            SSRSwitchName={ssrSwitchName}
            setSSRSwitchName={setSsrSwitchName}
          />
        </AdminEditorWrapper>
      ) : (
      <SpecsGrid>
        {(specs.length > 0 ? specs : [data]).map((spec, i) => {
          const ratedCurrent = spec?.current ?? '--';
          const actualCurrent = spec?.currentSSR ?? spec?.currentCurrent ?? '--';
          const wattage = spec?.wattage ?? '--';
          const voltage = spec?.voltage ?? '--';
          const length = spec?.lengths ?? '--';
          const lengthMetric = spec?.lengths ? (Number(spec.lengths) / 3.28048).toFixed(1) : null;
          const elemName = spec?.elementName || spec?.switchName || '';
          const partNum = spec?.partNumber || '';

          return (
            <SpecsRow key={i}>
              <DataCol>
                <DataLabel>CURRENT (A)</DataLabel>
                <DataBox $accent="current">

                  <CurrentStack>
                    <CurrentRated>{ratedCurrent}</CurrentRated>
                    <CurrentActual>{actualCurrent}</CurrentActual>
                  </CurrentStack>
                </DataBox>
              </DataCol>
              <DataCol>
                <DataLabel>WATTAGE (W)</DataLabel>
                <DataBox $accent="wattage">{wattage}</DataBox>
              </DataCol>
              <DataCol>
                <DataLabel>VOLTAGE (V)</DataLabel>
                <DataBox $accent="voltage">{voltage}</DataBox>
              </DataCol>
              <DataCol>
                <DataLabel>LENGTH (FT)</DataLabel>
                <DataBox $accent="length">{length === '--' ? '- -' : length}</DataBox>
              </DataCol>
              <DataCol $wide>
                <DataLabel>DESCRIPTION</DataLabel>
                <DescBox>
                  {elemName ? (
                    <>
                      <DescName>{elemName}</DescName>
                      {partNum && <DescPart>{partNum}</DescPart>}
                      <DescSpecs>
                        {ratedCurrent}A &middot; {wattage}W &middot; {voltage}V{lengthMetric ? ` · ${lengthMetric}m` : ''}{length !== '--' ? ` · ${length}ft` : ''}
                      </DescSpecs>
                    </>
                  ) : (
                    <DescName $muted>--</DescName>
                  )}
                </DescBox>
              </DataCol>
            </SpecsRow>
          );
        })}
      </SpecsGrid>
      )}

      {openPasswordBox && (
        <PasswordOverlay onClick={() => onClosePassword(ssrId)}>
          <ContainerLogin
            setIsSettingOpen={() => onConfirmSettings(ssrId)}
            handleClose={() => onClosePassword(ssrId)}
            isReadyToClose={true}
          />
        </PasswordOverlay>
      )}
    </SsrDetail>
  );
};



const Section = styled.div`
  margin: 0 20px;
  background: var(--switch-panel-bg-soft);
  border: 0.8px solid var(--switch-border-strong);
  border-radius: 16px;
  overflow: visible;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: var(--switch-text-faint);
`;

const LinkIcon = styled.svg`
  width: 16px;
  height: 16px;
  opacity: 0.7;
`;


const ExpandBtn = styled.button`
  	height: 36px;
  	position: relative;
  	box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.4);
  	border-radius: 26843500px;
  	background: var(--switch-panel-bg);
  	border: 0.8px solid var(--switch-border);
  	box-sizing: border-box;
  	display: flex;
  	align-items: center;
  	justify-content: center;
  	padding: 5px 10px;
  	text-align: center;
  	font-size: 12px;
  	color: var(--switch-text-muted);
  font-family: Inter;

  &:hover {
    color: var(--switch-text-primary);
    border-color: rgba(59, 130, 246, 0.4);
  }
`;

const ChipsRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 24px 20px;
  flex-wrap: nowrap;
`;

const SsrChip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
  height: 60px;
  padding: 0.8px 2px;
  background: #131c30;
  border: 0.8px solid #163567;
  border-radius: 10px;
  cursor: default;


  ${({ $canReset, $test, $warn }) => {
    if ($canReset) return css`
      border: 1px solid #ff4d4f;
      cursor: pointer;
      box-shadow: 0 0 6px rgba(255, 77, 79, 0.5);
    `;
    if ($test) return css`border: 2px solid #07a6dd;`;
    if ($warn) return css`border: 1px solid #ff7800;`;
    return '';
  }}

  &:disabled {
    cursor: default;
  }

  .light & {
    background: #dcdee2;
  }
`;

const ChipLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #cad5e2;
  white-space: nowrap;
  line-height: 13.5px;
  font-family: Inter, sans-serif;

  .light & {
    color: #0d1526;
  }
`;

const ChipBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-family: Consolas, monospace;
  line-height: 12px;
  text-transform: lowercase;
  height: 25px;
  width: 30px;
  ${({ $isOn, $isFault }) => {
    if ($isFault) return css`
      background: rgba(234, 5, 8, 0.39);
      color: red;
      .light & { color: #9f1818; }
    `;
    if ($isOn) return css`
      background: rgba(0, 188, 125, 0.35);
      color: #00d492;
      .light & { color: #005f42; }
    `;
    return css`
      background: rgba(234, 5, 8, 0.39);
      color: red;
      .light & { color: #9f1818; }
    `;
  }}
`;

const ExpandedArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 20px 24px;
`;

const SsrDetail = styled.div`
  background: var(--switch-panel-bg);
  border: 0.8px solid var(--switch-input-bg);
  border-radius: 12px;
  padding: 18px 20px;
  position: relative;


  ${({ $isFault, $overAmp, $isWarn }) => {
    if ($isFault) return css`border: 1px solid red;`;
    if ($overAmp) return css`border: 1px solid #ff7800;`;
    if ($isWarn) return css`border: 1px solid #ff7800;`;
    return '';
  }}
`;

const CurrentStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const CurrentRated = styled.span`
  color: #95ff45;
  font-size: 12px;
  line-height: 14px;
`;

const CurrentActual = styled.span`
  color: #e2e8f0;
  font-size: 12px;
  line-height: 14px;

  .light & {
    color: #0f172a;
  }
`;

const SettingsBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: 8px;
  border-radius: 8px;
  border: 0.8px solid var(--switch-border);
  background: var(--switch-panel-bg-alt);
  color: ${({ $active }) => ($active ? '#93c5fd' : 'var(--switch-text-faint)')};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: rgba(59, 130, 246, 0.4);
    color: var(--switch-text-primary);
  }
`;

const GearSvg = styled.svg`
  width: 15px;
  height: 15px;
`;

const AdminEditorWrapper = styled.div`
  position: relative;
`;

const PasswordOverlay = styled.div`
  position: absolute;
  top: -100px;
  right: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  padding: 16px;
  border-radius: 16px;
  cursor: pointer;
`;

const SsrDetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const SsrIdGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
`;

const SsrDetailName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--switch-text-primary);
  margin-right: 8px;
`;

const PanelLabel = styled.span`
  font-size: 10px;
  color: var(--switch-text-faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 2px;
  border-radius: 6px;
  overflow: hidden;
`;

const ToggleBtn = styled.button`
  padding: 4px 12px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;

  ${({ $active, $variant }) => {
    if ($active && $variant === 'off') return css`background: #EE4B56; color: #FFFFFF;`;
    if ($active && $variant === 'on') return css`background: #00BC7D; color: #FFFFFF;`;
    return css`background: var(--switch-button-hover-bg); color: var(--switch-text-faint);`;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TcDropdownWrapper = styled.div`
  margin-left: auto;
  position: relative;
`;

const TcButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 0.8px solid var(--switch-border);
  background: var(--switch-panel-bg-alt);
  color: var(--switch-text-faint);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: rgba(59, 130, 246, 0.4);
    color: var(--switch-text-primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ChevronSvg = styled.svg`
  width: 10px;
  height: 10px;
  transition: transform 0.15s;
  ${({ $open }) => $open && css`transform: rotate(180deg);`}
`;

const TcDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 120px;
  padding: 6px;
  border-radius: 10px;
  border: 0.8px solid rgba(59, 130, 246, 0.2);
  background: var(--switch-panel-bg);
  box-shadow: var(--switch-shadow-popover);
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TcOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'rgba(59, 130, 246, 0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#93c5fd' : 'var(--switch-text-muted)')};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.1s;

  &:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.08);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const TcDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: 1px solid ${({ $active }) => ($active ? '#3b82f6' : 'var(--switch-text-faint)')};
  background: ${({ $active }) => ($active ? '#3b82f6' : 'transparent')};
`;

const TcConfirmBtn = styled.button`
  margin-top: 4px;
  padding: 5px 8px;
  border-radius: 6px;
  border: none;
  background: #3b82f6;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: #2563eb;
  }
`;

const SpecsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SpecsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
`;

const DataCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $wide }) => $wide ? 'flex: 1; min-width: 0;' : 'flex-shrink: 0;'}
`;

const DataLabel = styled.span`
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--switch-text-faint);

  .light & {
    color: #475569;
  }
`;

const ACCENT_THEMES = {
  current: {
    border: 'rgba(91, 155, 213, 0.28)',
    shadow: '0px 2px 0px #5b9bd5 inset, 0px 0px 14px rgba(91, 155, 213, 0.08) inset, 0px 2px 6px rgba(0, 0, 0, 0.45)',
    bg: 'linear-gradient(180deg, #13233e, #0a1322)',
    lightBorder: 'rgba(43, 149, 210, 0.45)',
    lightShadow: '0px 2px 0px #2b95d2 inset, 0px 0px 14px rgba(43, 149, 210, 0.08) inset, 0px 1px 3px rgba(15, 23, 42, 0.08)',
    lightBg: 'linear-gradient(180deg, #eaf3fc, #ffffff)',
  },
  wattage: {
    border: 'rgba(255, 185, 0, 0.28)',
    shadow: '0px 2px 0px #ffb900 inset, 0px 0px 14px rgba(255, 185, 0, 0.08) inset, 0px 2px 6px rgba(0, 0, 0, 0.45)',
    bg: 'linear-gradient(180deg, #2b220f, #14110a)',
    lightBorder: 'rgba(217, 119, 6, 0.4)',
    lightShadow: '0px 2px 0px #d97706 inset, 0px 0px 14px rgba(217, 119, 6, 0.08) inset, 0px 1px 3px rgba(15, 23, 42, 0.08)',
    lightBg: 'linear-gradient(180deg, #fdf3dc, #ffffff)',
  },
  voltage: {
    border: 'rgba(0, 212, 146, 0.28)',
    shadow: '0px 2px 0px #00d492 inset, 0px 0px 14px rgba(0, 212, 146, 0.08) inset, 0px 2px 6px rgba(0, 0, 0, 0.45)',
    bg: 'linear-gradient(180deg, #0e2a22, #07140f)',
    lightBorder: 'rgba(5, 150, 105, 0.4)',
    lightShadow: '0px 2px 0px #059669 inset, 0px 0px 14px rgba(5, 150, 105, 0.08) inset, 0px 1px 3px rgba(15, 23, 42, 0.08)',
    lightBg: 'linear-gradient(180deg, #dcfaee, #ffffff)',
  },
  length: {
    border: 'rgba(167, 139, 250, 0.28)',
    shadow: '0px 2px 0px #a78bfa inset, 0px 0px 14px rgba(167, 139, 250, 0.08) inset, 0px 2px 6px rgba(0, 0, 0, 0.45)',
    bg: 'linear-gradient(180deg, #20193a, #100c1d)',
    lightBorder: 'rgba(124, 58, 237, 0.4)',
    lightShadow: '0px 2px 0px #7c3aed inset, 0px 0px 14px rgba(124, 58, 237, 0.08) inset, 0px 1px 3px rgba(15, 23, 42, 0.08)',
    lightBg: 'linear-gradient(180deg, #f1ecfe, #ffffff)',
  },
};

const FALLBACK_THEME = {
  border: 'var(--switch-border)',
  shadow: '0px 2px 6px rgba(0, 0, 0, 0.45)',
  bg: 'var(--switch-input-bg)',
  lightBorder: 'var(--switch-border)',
  lightShadow: '0px 1px 3px rgba(15, 23, 42, 0.08)',
  lightBg: 'var(--switch-input-bg)',
};

const DataBox = styled.div.attrs(({ $accent }) => {
  const t = ACCENT_THEMES[$accent] || FALLBACK_THEME;
  return {
    style: {
      '--db-border': t.border,
      '--db-shadow': t.shadow,
      '--db-bg': t.bg,
      '--db-light-border': t.lightBorder,
      '--db-light-shadow': t.lightShadow,
      '--db-light-bg': t.lightBg,
    },
  };
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 56px;
  border-radius: 6px;
  border: 0.8px solid var(--db-border);
  background: var(--db-bg);
  box-shadow: var(--db-shadow);
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
  font-family: Consolas, monospace;
  letter-spacing: 0.5px;
  line-height: 14px;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  flex-shrink: 0;

  .light & {
    background: var(--db-light-bg);
    box-shadow: var(--db-light-shadow);
    border-color: var(--db-light-border);
    color: #0f172a;
  }
`;

const DescBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  height: 56px;
  padding: 8px 9px 1px;
  border-radius: 6px;
  border: 0.8px solid rgba(148, 163, 184, 0.22);
  background: linear-gradient(180deg, #1a2438, #0c111c);
  box-shadow: 0px 2px 0px rgba(148, 163, 184, 0.6) inset, 0px 0px 14px rgba(148, 163, 184, 0.05) inset, 0px 2px 6px rgba(0, 0, 0, 0.45);
  justify-content: center;
  overflow: hidden;
  font-family: Consolas, monospace;

  .light & {
    background: linear-gradient(180deg, #f1f5f9, #ffffff);
    box-shadow: 0px 2px 0px rgba(71, 85, 105, 0.5) inset, 0px 0px 14px rgba(71, 85, 105, 0.05) inset, 0px 1px 3px rgba(15, 23, 42, 0.08);
    border-color: rgba(71, 85, 105, 0.3);
  }
`;

const DescName = styled.span`
  font-size: 9px;
  font-weight: 500;
  color: ${({ $muted }) => ($muted ? 'var(--switch-text-faint)' : '#e2e8f0')};
  letter-spacing: 0.2px;
  line-height: 11px;
  font-family: Inter, sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .light & {
    color: ${({ $muted }) => ($muted ? 'var(--switch-text-faint)' : '#0f172a')};
  }
`;

const DescPart = styled.span`
  font-size: 8px;
  font-weight: 400;
  color: #90a1b9;
  font-family: Consolas, monospace;
  letter-spacing: 0.2px;
  line-height: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .light & {
    color: #475569;
  }
`;

const DescSpecs = styled.span`
  font-size: 7.5px;
  font-weight: 400;
  color: #62748e;
  font-family: Consolas, monospace;
  letter-spacing: 0.3px;
  line-height: 11px;

  .light & {
    color: #64748b;
  }
`;
